import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, X, Sparkles, ArrowLeft } from 'lucide-react';
import { Textarea } from './ui/textarea';
import ReactMarkdown from 'react-markdown';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { CHAT_ROLE } from '../constants/chat-roles';
import { ChatMessage } from '../interfaces/chat-message';

interface ChatQuestion {
  id: string;
  type: string;
  question: string;
  options: string[] | string | null;
  correctAnswer: string | number | number[];
  level: number;
  explanation: string;
  difficulty: string;
}

interface ChatLevelInfo {
  level: number;
  name: string;
  description: string;
}

export interface QuestionEdit {
  questionId: string;
  field: string;
  newValue: unknown;
  reason: string;
}

interface EditQuestionsChatProps {
  levelId: string;
  questions: ChatQuestion[];
  levelInfo: ChatLevelInfo;
  onEditsReceived: (edits: QuestionEdit[]) => void;
  focusedQuestionId?: string;
  onClose: () => void;
}

// Add a helper function to process AI messages
const processAIMessage = (message: string) => {
  // Remove question ID references
  return message.replace(/Question ID: \d+/g, 'Question');
};

export default function EditQuestionsChat({
  levelId,
  questions,
  levelInfo,
  onEditsReceived,
  focusedQuestionId,
  onClose,
}: EditQuestionsChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [previousContext, setPreviousContext] = useState<{
    levelId?: string;
    focusedQuestionId?: string;
  }>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-adjust textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`; // Max 5 lines (approximately)
    }
  }, [input]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getInitialMessage = () => {
    if (focusedQuestionId) {
      const focusedQuestion = questions.find(
        (q) => q.id.toString() === focusedQuestionId,
      );
      if (focusedQuestion) {
        return `I'm here to help you edit your questions. You're currently focused on this question:\n\n"${focusedQuestion.question}"\n\nWhat would you like to change?`;
      }
    }
    return `I'm here to help you edit the questions for Level ${levelInfo.level}. What would you like to change?`;
  };

  useEffect(() => {
    // Check if we're switching contexts
    if (levelId !== previousContext.levelId) {
      // Different level - clear chat and start fresh
      setMessages([
        { role: CHAT_ROLE.ASSISTANT, content: getInitialMessage() },
      ]);
      setConversationId(null);
    } else if (focusedQuestionId !== previousContext.focusedQuestionId) {
      // Same level, different question - add context switch message
      const focusedQuestion = questions.find(
        (q) => q.id.toString() === focusedQuestionId,
      );
      if (focusedQuestion) {
        setMessages((prev) => [
          ...prev,
          {
            role: CHAT_ROLE.SYSTEM,
            content: `Switching focus to question: "${focusedQuestion.question}"`,
          },
        ]);
      }
    }

    // Update previous context
    setPreviousContext({ levelId, focusedQuestionId });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, focusedQuestionId]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
    }

    setMessages((prev) => [
      ...prev,
      { role: CHAT_ROLE.USER, content: userMessage },
    ]);

    try {
      const response = await apiFetch(ENDPOINTS.QUESTION_EDITING, {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          message: userMessage,
          questions,
          levelInfo,
          focusedQuestionId,
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();

      // Store conversationId for subsequent messages
      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      setMessages((prev) => [
        ...prev,
        { role: CHAT_ROLE.ASSISTANT, content: data.conversation },
      ]);

      if (data.edits?.length > 0) {
        onEditsReceived(data.edits);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (message: ChatMessage) => {
    if (message.role === CHAT_ROLE.SYSTEM) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
          <Sparkles className="h-3.5 w-3.5" />
          {message.content}
          <Sparkles className="h-3.5 w-3.5" />
        </div>
      );
    }

    const content =
      message.role === CHAT_ROLE.ASSISTANT
        ? processAIMessage(message.content)
        : message.content;

    return (
      <div
        className={`max-w-[80%] rounded-lg p-3 ${
          message.role === CHAT_ROLE.ASSISTANT
            ? 'bg-[#FDF8F3] prose prose-sm dark:prose-invert'
            : 'bg-[#A78BFA] text-white'
        }`}
      >
        {message.role === CHAT_ROLE.ASSISTANT ? (
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
              ul: ({ children }) => (
                <ul className="mb-4 list-disc pl-4 last:mb-0">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-4 list-decimal pl-4 last:mb-0">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="mb-1 last:mb-0">{children}</li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
              em: ({ children }) => <em className="italic">{children}</em>,
              h1: ({ children }) => (
                <h1 className="text-lg font-bold mb-4 last:mb-0">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-base font-bold mb-3 last:mb-0">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-bold mb-2 last:mb-0">{children}</h3>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        ) : (
          content
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h3 className="font-semibold">{levelInfo.name}</h3>
            {focusedQuestionId && (
              <p className="text-sm text-muted-foreground">
                Editing specific question
              </p>
            )}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Chat Area */}
      <ScrollArea className="flex-grow p-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === CHAT_ROLE.SYSTEM
                  ? 'justify-center'
                  : message.role === CHAT_ROLE.ASSISTANT
                    ? 'justify-start'
                    : 'justify-end'
              }`}
            >
              {renderMessage(message)}
            </div>
          ))}

          {/* AI Typing Indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-lg p-3 bg-secondary text-secondary-foreground">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="h-2 w-2 bg-current rounded-full animate-bounce"></div>
                  <span className="text-xs ml-2">Llaami is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setInput(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Type your message..."
            disabled={isLoading}
            className="min-h-[40px] max-h-[120px] resize-none"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="self-end bg-[#A78BFA] text-white hover:bg-[#A78BFA]"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
